import React, { useMemo, useCallback } from 'react'
import {
	LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import useMediaQuery from '../../store/hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { DOUBLE_RESPONSIVE_CONTAINER_MIN_SIZE } from '../../constants/constants';


const WaterContentChart: React.FC<{ data: { x: number, y: number }[], isLoading: boolean }> = ({ data, isLoading: _isLoading }) => {
	const isMobile = useMediaQuery('(max-width: 900px)');
	const isEmpty = data.length === 0;

	const { minX, maxX, minY, maxY, xTicks, yTicks } = useMemo(() => {
		if (isEmpty) {
			const minX = 0, maxX = 100, minY = 0, maxY = 25;
			const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
			const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
			return { minX, maxX, minY, maxY, xTicks, yTicks };
		}
		const minX = Math.min(...data.map(d => d.x));
		const maxX = Math.max(...data.map(d => d.x));
		const minY = Math.min(...data.map(d => d.y));
		const maxY = Math.max(...data.map(d => d.y));
		const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
		const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
		return { minX, maxX, minY, maxY, xTicks, yTicks };
	}, [data, isEmpty]);

	const tooltipLabelFormatter = useCallback((value: number) => value.toFixed(3), []);
	const tooltipFormatter = useCallback((value: number) => [value.toFixed(3)], []);
	const { t } = useTranslation("water_content");

	return (
		<div className="parabolic-curves">
			<ResponsiveContainer width="100%" height={300} {...DOUBLE_RESPONSIVE_CONTAINER_MIN_SIZE}>
				<LineChart data={isEmpty ? [{ x: minX, y: minY }] : data} margin={{
					top: 20,
					right: 30,
					left: 30,
					bottom: 40
				}}>
					<CartesianGrid strokeDasharray="0"
						horizontal={true}
						vertical={true}
					/>
					<XAxis
						axisLine={true}
						dataKey="x"
						domain={[minX, maxX]}
						label={{ value: t("chart_labels.x"), position: 'insideBottom', offset: -10 }}
						type="number"
						ticks={xTicks}
						tickFormatter={(value: number) => value.toFixed(0)}
					/>
					<YAxis
						axisLine={true}
						domain={[minY, maxY]}
						ticks={yTicks}
						label={{ value: t("chart_labels.y"), angle: -90, position: 'insideLeft', offset: -17 }}
						tickFormatter={(value: number) => value > 1000 ? value.toExponential(2) : value.toFixed(2)}
					/>
					<Tooltip
						labelFormatter={tooltipLabelFormatter}
						formatter={tooltipFormatter}
						wrapperStyle={{ pointerEvents: 'none' }}
						isAnimationActive={false}
					/>
					<Legend
						formatter={(value: number) => value}
						layout={isMobile ? 'horizontal' : 'vertical'}
						verticalAlign={isMobile ? 'bottom' : 'top'}
						align={isMobile ? 'center' : 'right'}
					/>

					<Line
						type="monotone"
						dataKey="y"
						stroke={isEmpty ? "transparent" : "#4c23deff"}
						strokeWidth={1}
						name={t("chart_labels.w_line")}
						dot={false}
						activeDot={false}
						legendType="plainline"
					/>

				</LineChart>
			</ResponsiveContainer>
		</div >
	)
}

export default React.memo(WaterContentChart)